import { useEffect, useState } from "react";
import Content from "../../../../layout/Content";
import listHandler from "@serie3/common/Domain/listHandler";
import CrudApi from "@serie3/common/api/crudApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd, faXmark, faDownload } from "@fortawesome/free-solid-svg-icons";
import DocumentsHeader from "../DocumentsHeader";
import NewModal from "../modals/NewModal";
import DocumentCard from "../DocumentCard";
import FileDropzone from "../modals/FileDropzone";
import DocumentsConfig from "@serie3/freelancesplaceapi/Config/Documents/DocumentsConfig";
import { btn, btnHoverStyle } from "../../../../layout/Buttons/buttonStyles";
import PaymentModal from "../../../../layout/Payment/PaymentModal";
import getSettings from "@serie3/common/config/settings";

const shimmerCss = `
  @keyframes shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
  .skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 4px;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const SkeletonCard = () => (
  <div style={{ background: "#fff", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", overflow: "hidden" }}>
    <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid #f0f0f0" }}>
      <div className="skeleton" style={{ height: "14px", width: "60%", borderRadius: "4px" }} />
    </div>
    <div className="skeleton" style={{ height: 160 }} />
    <div style={{ padding: "14px" }}>
      <div className="skeleton" style={{ height: "10px", width: "70%", marginBottom: "8px" }} />
      <div className="skeleton" style={{ height: "10px", width: "40%" }} />
    </div>
  </div>
);

const Spinner = () => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 0" }}>
    <div style={{ border: "3px dotted #038edc", borderRadius: "50%", width: "48px", height: "48px", animation: "spin 0.9s linear infinite" }} />
  </div>
);

const Index = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRowError, setShowRowError] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [settings, setSettings] = useState();

  const [showModal, setShowModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [textContent, setTextContent] = useState(null);
  const [previewDocId, setPreviewDocId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    const response = await CrudApi.getAll("/documents/");
    setData(response.data.documents);
    setSettings(getSettings());
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (previewFile?.type?.startsWith("text/")) {
      fetch(previewFile.url).then((r) => r.text()).then(setTextContent);
    }
  }, [previewFile]);

  useEffect(() => {
    if (!showModal && previewFile?.url) {
      URL.revokeObjectURL(previewFile.url);
      setPreviewFile(null);
      setTextContent(null);
    }
  }, [showModal]);

  const showFile = async (document) => {
    setPreviewFile(null);
    setTextContent(null);
    setPreviewTitle(document.name);
    setPreviewDocId(document.id);
    setFileLoading(true);
    setShowModal(true);

    const response = await CrudApi.getBinary(DocumentsConfig.endpoints.preview + document.id);
    if (response?.data) {
      const contentType = response.headers?.["content-type"] || "application/octet-stream";
      const url = URL.createObjectURL(response.data);
      setPreviewFile({ url, type: contentType });
    }
    setFileLoading(false);
  };

  const NewButton = () => (
    <>
      <style>{btnHoverStyle}</style>
      <button className="btn-modern" style={{ ...btn.base, ...btn.success }} onClick={() => setShowNew(true)}>
        <FontAwesomeIcon icon={faAdd} /> New
      </button>
    </>
  );

  const Title = () => "Documents";

  const deleteFromList = (document) => {
    const itemIndex = data.findIndex((obj) => obj.id === document.id);
    listHandler.deleteFromList(itemIndex, CrudApi.del(DocumentsConfig.endpoints.delete, document.id), setData, data, setShowRowError);
  };

  return (
    <>
      <style>{shimmerCss}</style>

      <NewModal show={showNew} setShow={setShowNew}>
        <FileDropzone data={data} setShowPayModal={setShowPayModal} setData={(updated) => { setData(updated); setShowNew(false); }} />
      </NewModal>

      {settings && <PaymentModal show={showPayModal} setShow={setShowPayModal} priceTableId={settings.pricingTables.document} />}

      {/* Preview modal */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: "14px", width: "100%", maxWidth: "860px", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          >
            {/* Modal header */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "#212121", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "calc(100% - 80px)" }}>
                {previewTitle}
              </span>
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                {previewFile && (
                  <a
                    href={previewFile.url}
                    download={previewTitle}
                    style={{ background: "#e8f0fe", color: "#1a73e8", border: "none", borderRadius: "8px", width: "32px", height: "32px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.85rem", textDecoration: "none" }}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </a>
                )}
                <button
                  onClick={() => setShowModal(false)}
                  style={{ background: "#f5f5f5", border: "none", borderRadius: "8px", width: "32px", height: "32px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#757575", fontSize: "0.9rem" }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>

            {/* Modal body */}
            <div style={{ flex: 1, overflow: "auto", padding: fileLoading ? 0 : "16px" }}>
              {fileLoading ? (
                <Spinner />
              ) : (
                <>
                  {previewFile?.type?.startsWith("image/") && (
                    <img src={previewFile.url} alt={previewTitle} style={{ maxWidth: "100%", display: "block", margin: "0 auto", borderRadius: "8px" }} />
                  )}
                  {previewFile?.type === "application/pdf" && (
                    <iframe src={previewFile.url} title={previewTitle} width="100%" height="700px" style={{ border: "none", borderRadius: "8px" }} />
                  )}
                  {previewFile?.type?.startsWith("text/") && (
                    <pre style={{ margin: 0, fontSize: "0.85rem", color: "#212121", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{textContent}</pre>
                  )}
                  {previewFile?.type?.startsWith("video/") && (
                    <video controls width="100%" style={{ borderRadius: "8px" }}>
                      <source src={previewFile.url} type={previewFile.type} />
                    </video>
                  )}
                  {previewFile && !["image/", "application/pdf", "text/", "video/"].some((p) => previewFile.type?.startsWith(p)) && (
                    <div style={{ textAlign: "center", padding: "40px 0" }}>
                      <a href={previewFile.url} download={previewTitle} style={{ ...btn.base, ...btn.primary, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                        <FontAwesomeIcon icon={faDownload} /> Download file
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <DocumentsHeader />
      <Content Title={Title} AdditionalButtons={NewButton}>
        {loading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "16px", padding: "4px 0" }}>
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#9e9e9e", fontSize: "0.95rem" }}>
            No documents yet. Upload your first file!
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "16px", padding: "4px 0" }}>
            {data.map((document) => (
              <DocumentCard
                key={document.id}
                document={document}
                deleteFunction={deleteFromList}
                onPreview={showFile}
              />
            ))}
          </div>
        )}
      </Content>
    </>
  );
};

export default Index;
